import * as React from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import {
  IconBold,
  IconItalic,
  IconLink,
  IconList,
  IconOrderedList,
} from "../icons";
import type { CollaborateUser } from "../../types/collaborate";
import {
  CollaborateEditorExtension,
  setPlaybookPending,
  setSignoffPending,
} from "../../utils/collaborateEditorStorage";
import { CollaboratePlaybookBadgeExtension } from "../../utils/collaboratePlaybookBadgeExtension";
import { createPlaybookMentionExtension } from "../../utils/collaboratePlaybookExtension";
import {
  hasPlaybookBadge,
  removePlaybookChips,
} from "../../utils/collaboratePlaybookChips";
import { createCollaborateMentionSuggestion } from "../../utils/collaborateMentionSuggestion";
import { CollaborateSignoffBadgeExtension } from "../../utils/collaborateSignoffBadgeExtension";
import { hasSignoffBadge, removeSignoffChips } from "../../utils/collaborateSignoffChips";
import { createCollaborateSlashExtension } from "../../utils/collaborateSlashExtension";
import { createSignoffMentionExtension } from "../../utils/collaborateSignoffExtension";
import { serializeEditorBody } from "../../utils/serializeEditorBody";
import { getMentionIdsFromEditor } from "../../utils/parseMentions";
import { getSignoffAssigneeFromEditor } from "../../utils/parseSignoff";
import type { CollaborateUserProfile, CollaborateUserRef, CollaborateAgreementVersion } from "../../types/collaborateThread";
import "./CollaborateCommentEditor.scss";
import "tippy.js/dist/tippy.css";

export type CollaborateCommentEditorHandle = {
  focus: () => void;
  clear: () => void;
  getHtml: () => string;
  getText: () => string;
  /** API body with `<signoff>` and `<mention>` tags. */
  getSerializedBody: () => string;
  /** Sign-off assignee from editor chip attrs (uses user id, not display name). */
  getSignoffAssignee: (users: CollaborateUserProfile[]) => CollaborateUserRef | null;
  /** Mentioned user IDs from editor mention nodes (uses user id, not display name). */
  getMentionIds: (users: CollaborateUserProfile[]) => string[];
  isEmpty: () => boolean;
  isSignoffPending: () => boolean;
  isPlaybookPending: () => boolean;
  getEditor: () => Editor | null;
};

export type CollaborateCommentEditorProps = {
  users: CollaborateUser[];
  disabled?: boolean;
  placeholder?: string;
  /** Enables / commands and /sign-off → @ flow. */
  enableSlashCommands?: boolean;
  /** Required for /update-playbook — true when document text is selected. */
  hasDocumentSelection?: () => boolean;
  /** Hides list/link toolbar rows — for inline composer. */
  compact?: boolean;
  /** Only bold + italic when compact. */
  minimalToolbar?: boolean;
  versions?: CollaborateAgreementVersion[];
  selectedVersionId?: string;
  onVersionChange?: (versionId: string) => void;
  onChange?: (payload: { html: string; text: string; isEmpty: boolean }) => void;
  onSubmit?: () => void;
};

type ToolbarActiveState = {
  isBold: boolean;
  isItalic: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
  isLink: boolean;
};

/** Re-render toolbar when selection or marks change (TipTap does not trigger React renders). */
function useEditorToolbarActive(editor: Editor | null): ToolbarActiveState {
  const [, refresh] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (!editor) {
      return undefined;
    }
    const update = () => refresh();
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  if (!editor) {
    return {
      isBold: false,
      isItalic: false,
      isBulletList: false,
      isOrderedList: false,
      isLink: false,
    };
  }

  return {
    isBold: editor.isActive("bold"),
    isItalic: editor.isActive("italic"),
    isBulletList: editor.isActive("bulletList"),
    isOrderedList: editor.isActive("orderedList"),
    isLink: editor.isActive("link"),
  };
}

const CollaborateCommentEditor = React.forwardRef<
  CollaborateCommentEditorHandle,
  CollaborateCommentEditorProps
>(
  (
    {
      users,
      disabled = false,
      placeholder = "Write your comment…",
      enableSlashCommands = false,
      hasDocumentSelection = () => false,
      compact = false,
      minimalToolbar = false,
      versions = [],
      selectedVersionId = "",
      onVersionChange,
      onChange,
      onSubmit,
    },
    ref
  ) => {
    const usersRef = React.useRef(users);
    usersRef.current = users;
    const hasDocumentSelectionRef = React.useRef(hasDocumentSelection);
    hasDocumentSelectionRef.current = hasDocumentSelection;
    const onSubmitRef = React.useRef(onSubmit);
    onSubmitRef.current = onSubmit;
    const sortedVersions = React.useMemo(
      () => [...versions].sort((a, b) => b.version - a.version),
      [versions],
    );
    const showVersionSelect = sortedVersions.length > 0;

    const editor = useEditor({
      extensions: [
        CollaborateEditorExtension,
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
        }),
        Placeholder.configure({ placeholder }),
        Mention.configure({
          HTMLAttributes: { class: "collaborate-mention" },
          renderLabel: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
          suggestion: createCollaborateMentionSuggestion(() => usersRef.current),
        }),
        ...(enableSlashCommands
          ? [
              CollaborateSignoffBadgeExtension,
              CollaboratePlaybookBadgeExtension,
              createSignoffMentionExtension(() => usersRef.current),
              createPlaybookMentionExtension(() => usersRef.current),
              createCollaborateSlashExtension(() => hasDocumentSelectionRef.current()),
            ]
          : []),
      ],
      editable: !disabled,
      editorProps: {
        attributes: {
          class: "collaborate-comment-editor__content",
          "aria-label": "Comment",
        },
        handleKeyDown: (_view, event) => {
          if (
            event.key !== "Enter" ||
            event.shiftKey ||
            event.isComposing ||
            !onSubmitRef.current ||
            document.querySelector(".collaborate-mention-popover")
          ) {
            return false;
          }
          event.preventDefault();
          onSubmitRef.current?.();
          return true;
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML();
        const text = ed.getText();
        onChange?.({ html, text, isEmpty: text.trim().length === 0 });
      },
    });

    React.useEffect(() => {
      editor?.setEditable(!disabled);
    }, [disabled, editor]);

    React.useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.commands.focus("end"),
        clear: () => {
          if (editor) {
            editor.commands.clearContent(true);
            removeSignoffChips(editor);
            removePlaybookChips(editor);
            setSignoffPending(editor, false);
            setPlaybookPending(editor, false);
          }
        },
        getHtml: () => editor?.getHTML() ?? "",
        getText: () => editor?.getText() ?? "",
        getSerializedBody: () => (editor ? serializeEditorBody(editor) : ""),
        getSignoffAssignee: (users) =>
          editor ? getSignoffAssigneeFromEditor(editor, users) : null,
        getMentionIds: (users) => (editor ? getMentionIdsFromEditor(editor, users) : []),
        isEmpty: () => (editor?.getText() ?? "").trim().length === 0,
        isSignoffPending: () => (editor ? hasSignoffBadge(editor) : false),
        isPlaybookPending: () => (editor ? hasPlaybookBadge(editor) : false),
        getEditor: () => editor,
      }),
      [editor]
    );

    const setLink = React.useCallback(() => {
      if (!editor) {
        return;
      }
      const previous = editor.getAttributes("link").href as string | undefined;
      const url = window.prompt("Link URL", previous ?? "https://");
      if (url === null) {
        return;
      }
      if (url === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }, [editor]);

    const toolbarActive = useEditorToolbarActive(editor);

    if (!editor) {
      return null;
    }

    const showFullToolbar = !compact;
    const showLists = showFullToolbar && !minimalToolbar;
    const showLink = showFullToolbar && !minimalToolbar;

    const fullToolbar = showFullToolbar ? (
      <div className="collaborate-comment-editor__toolbar" role="toolbar" aria-label="Formatting">
        <div className="collaborate-comment-editor__toolbar-main">
          <ToolbarButton
            title="Bold"
            active={toolbarActive.isBold}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <IconBold size={14} />
          </ToolbarButton>
          <ToolbarButton
            title="Italic"
            active={toolbarActive.isItalic}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <IconItalic size={14} />
          </ToolbarButton>
          {showLists ? (
            <>
              <span className="collaborate-comment-editor__toolbar-divider" aria-hidden />
              <ToolbarButton
                title="Bullet list"
                active={toolbarActive.isBulletList}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <IconList size={14} />
              </ToolbarButton>
              <ToolbarButton
                title="Numbered list"
                active={toolbarActive.isOrderedList}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <IconOrderedList size={14} />
              </ToolbarButton>
            </>
          ) : null}
          {showLink ? (
            <>
              <span className="collaborate-comment-editor__toolbar-divider" aria-hidden />
              <ToolbarButton
                title="Link"
                active={toolbarActive.isLink}
                disabled={disabled}
                onClick={setLink}
              >
                <IconLink size={14} />
              </ToolbarButton>
            </>
          ) : null}
        </div>
        {showVersionSelect ? (
          <label className="collaborate-comment-editor__version-select">
            <span className="collaborate-comment-editor__version-label">Tag to</span>
            <select
              value={selectedVersionId}
              onChange={(event) => onVersionChange?.(event.target.value)}
              disabled={disabled}
              aria-label="Tag comment to agreement version"
            >
              {sortedVersions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.label ?? `v${version.version}`}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    ) : null;

    return (
      <div
        className={`collaborate-comment-editor${disabled ? " collaborate-comment-editor--disabled" : ""}${compact ? " collaborate-comment-editor--compact" : ""}`}
      >
        {showFullToolbar ? fullToolbar : null}
        <div className="collaborate-comment-editor__body">
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }
);

CollaborateCommentEditor.displayName = "CollaborateCommentEditor";

type ToolbarButtonProps = {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  title,
  active,
  disabled,
  onClick,
  children,
}) => (
  <button
    type="button"
    className={`collaborate-comment-editor__toolbar-btn${active ? " collaborate-comment-editor__toolbar-btn--active" : ""}`}
    title={title}
    aria-label={title}
    aria-pressed={Boolean(active)}
    disabled={disabled}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
  >
    {children}
  </button>
);

export default CollaborateCommentEditor;
